import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "@fluentui/react-components";
import {
  useAuth,
  fetchProperties,
  fetchInitialData,
  postUserMessage,
  postFeedback,
  ChatMessage,
  ComposeBox,
  formatEmailText,
  cleanDraftText,
  markdownToHtml,
  prepareRichDraftHtml,
  formatUserMessage,
  mainStyle,
  useMessages,
  postUsage,
  postUserTone,
  selectUserStyle,
  useFileContext,
  useFileUpload,
  buildFileUsageMeta,
  openResourceView,
  buildResourceReference,
} from "common";
import "./Scrollbar.css";
import PropTypes from "prop-types";

const getOfficeItem = (officeType, currentItem) => {
  if (currentItem) return currentItem;
  if (officeType === "OUTLOOK") return Office.context?.mailbox?.item ?? null;
  return Office.context?.document ?? null;
};

const UnicliChat = ({ currentItem, officeType, currentUser }) => {
  const item = getOfficeItem(officeType, currentItem);
  const isReplyWindow =
    officeType === "OUTLOOK" &&
    item?.subject != null &&
    typeof item.subject.setAsync !== "function";

  const [residents, setResidents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [userInput, setUserInput] = useState("");
  const hasFetchedInitialData = useRef(false);
  const userPromptArea = useRef(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const selectedOptionsRef = useRef(selectedOptions);
  const [selectedValue, setSelectedValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(0);
  const [selectedTab, setSelectedTab] = useState(isReplyWindow ? "replyall" : "new");
  const [feedbackMessageId, setFeedbackMessageId] = useState(0); // Message ID for feedback
  const lastMessageRef = useRef(null);
  const [checkedTone, setCheckedTone] = useState(currentUser?.tone || "Casual");
  const { authState, login, logout } = useAuth();
  const fileContext = useFileContext(authState?.authToken, selectedOptions[0]);
  const { getFileIds, runWithFileValidation } = fileContext;
  const [searchChecked, setSearchChecked] = useState(true);
  const [userStyles, setUserStyles] = useState(Array.isArray(currentUser?.style) ? currentUser.style : []);
  const [selectedStyleName, setSelectedStyleName] = useState(() => {
    const selected = (Array.isArray(currentUser?.style) ? currentUser.style : []).find((s) => s.selected);
    return selected?.name || (Array.isArray(currentUser?.style) && currentUser.style[0]?.name) || "Default";
  });
  const [selectedStyleContent, setSelectedStyleContent] = useState(() => {
    const selected = (Array.isArray(currentUser?.style) ? currentUser.style : []).find((s) => s.selected);
    return selected?.content || "";
  });
  useEffect(() => {
    const stylesArr = Array.isArray(currentUser?.style) ? currentUser.style : [];
    setUserStyles(stylesArr);
    const selected = stylesArr.find((s) => s.selected);
    setSelectedStyleName(selected?.name || (stylesArr[0]?.name) || "Default");
    setSelectedStyleContent(selected?.content || "");
  }, [currentUser]);
  const styles = mainStyle();
  // Loader text from poll status
  const [loadingStageText, setLoadingStageText] = useState("");
  const handlePollProgress = (statusData) => {
    if (statusData?.message) {
      setLoadingStageText(statusData.message);
    }
  };
  const [hasSelectionForRewrite, setHasSelectionForRewrite] = useState(false);
  const [plainTextChecked, setPlainTextChecked] = useState(false);
  const prevRewriteModeRef = useRef(false);

  const processFeedback = (messageId, feedback) => {
    if (feedback === "down") {
      setFeedbackMessageId(messageId);
      handleOpenFeedback();
    } else {
      handleFeedback(messageId, feedback);
    }
  };

  const { messages, addMessage, resetMessages } = useMessages(processFeedback);

  const handleCloseFeedback = () => setIsFeedbackOpen(false);
  const handleOpenFeedback = () => setIsFeedbackOpen(true);

  const handleSendMessage = async (event) => {
    const executeSend = () => {
      if (selectedTab === "chat") {
        if (!userInput.trim()) {
          return;
        }
        setIsLoading(true);
        addMessage(formatUserMessage(userInput, selectedTab, officeType), "user");
        const messageToSend = userInput;
        setUserInput("");
        sendToBot(messageToSend);
        return;
      }
      addMessage(formatUserMessage(userInput, selectedTab, officeType), "user");
      handleDraft(userInput);
    };

    await runWithFileValidation(executeSend, { onDocContext: postDocContextUsage });
    event.currentTarget.blur();
  };

  const handleBotResponse = (response, references, messageId) => {
    console.log("Bot response:", response);
    response = markdownToHtml(response);
    addMessage(response, "bot", { links: references, buttions: [], messageId: messageId });
    setIsLoading(false);
    setUserInput(""); // Clear input after bot response
  };

  const sendToBot = async (userMessage) => {
    try {
      const text = await getWordContent();
      // property_id, conversation_id, content, authToken, source, mode, document
      const data = await postUserMessage(
        selectedOptions[0],
        conversationId,
        userMessage,
        authState.authToken,
        "word",
        "chat",
        text,
        "",
        "",
        !searchChecked,
        getFileIds(),
        handlePollProgress
      );
      console.log(data);
      if (data.status_code === 200) {
        console.log("Message sent to bot successfully");
        setConversationId(data.response.conversation_id);
        handleBotResponse(data.response.answer, data.response.references, data.response.id);
      } else if (data.status_code === 422) {
        console.log("Moderation applied to the message");
        handleBotResponse(data.message);
      } else {
        console.log("Error sending message to bot");
        handleBotResponse("Sorry, I couldn't process your message. Please try again.");
      }
    } catch (error) {
      console.error("Failed to send message to bot:", error);
      handleBotResponse("Sorry, I couldn't process your message. Please try again.");
    }
  };
  const onPostUsage = async (activity, category, meta) => {
    try {
      const data = await postUsage(activity, category, meta, authState.authToken);
      console.log("Usage data posted successfully");
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      } else {
        console.error("Failed to post usage data:", error);
      }
    }
  };

  const getFileUsageContext = () => ({
    mode: selectedTab === "chat" ? "chat" : officeType === "WORD" ? "composedoc" : selectedTab,
    reply: isReplyWindow,
    source: officeType === "WORD" ? "word" : "outlook",
    propertyId: fileContext.propertyId,
  });

  const postDocContextUsage = (files) => {
    try {
      const { mode, reply, source, propertyId } = getFileUsageContext();
      onPostUsage("doc-context", "add-in-use", buildFileUsageMeta({ mode, reply, source, propertyId, files }));
    } catch (e) {
      console.warn("Failed to post usage for doc-context:", e);
    }
  };

  const postUploadUsage = (files, activity = "upload") => {
    try {
      const { mode, reply, source, propertyId } = getFileUsageContext();
      onPostUsage(activity, "add-in-use", buildFileUsageMeta({ mode, reply, source, propertyId, files }));
    } catch (e) {
      console.warn(`Failed to post usage for ${activity}:`, e);
    }
  };

  const fileUpload = useFileUpload(authState?.authToken, fileContext.propertyId, {
    onUploadSuccess: () => fileContext.loadResources(),
    onUploadUsage: postUploadUsage,
  });

  const buildReferenceUsageMeta = (reference) => ({
    mode: "chat",
    reply: false,
    detail: reference?.name || "",
    source: "word",
    tokens: 0,
    property_id: 0,
    prompt_tokens: 0,
    completion_tokens: 0,
  });

  const handlePostReferenceUsage = (activity, reference) => {
    try {
      onPostUsage(activity, "add-in-use", buildReferenceUsageMeta(reference));
    } catch (e) {
      console.warn(`Failed to post usage for ${activity}:`, e);
    }
  };

  const handleViewResource = async (resource) => {
    try {
      await openResourceView(resource, authState?.authToken);
      handlePostReferenceUsage("reference-view", buildResourceReference(resource));
    } catch (error) {
      console.error("Failed to open file view:", error);
    }
  };

  const onNewTopic = () => {
    resetMessages();
    setUserInput("");
    setConversationId(0);
    var initialMessageText = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
      with the document`;
    if (selectedValue !== "" && selectedValue != undefined) {
      initialMessageText += ` in the context of ${selectedValue}`;
    }
    initialMessageText += `. What would you like to do?`;
    addMessage(initialMessageText, "bot");
  };

  const handleFeedback = (messageId, feedback) => {
    // Send feedback to the backend
    postFeedback(messageId, feedback, authState.authToken);
  };

  const onSearchCheckedChange = React.useCallback(
    (ev) => {
      console.log("Search checked:", ev.currentTarget.checked);
      setSearchChecked(ev.currentTarget.checked);
    },
    [searchChecked]
  );
  const onPlainTextCheckedChange = React.useCallback((ev) => {
    setPlainTextChecked(ev.currentTarget.checked);
  }, []);
  const updateUserTone = async (tone) => {
    try {
      const data = await postUserTone(authState.authToken, tone);
      console.log("User tone updated successfully");
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      }
      console.error("Failed to update user tone:", error);
    }
    setCheckedTone(tone);
  };
  const updateUserStyle = async (styleName) => {
    try {
      const data = await selectUserStyle(authState.authToken, styleName);
      console.log("User style selected successfully", data);
      setUserStyles((prev) => {
        const updated = (prev || []).map((s) => ({ ...s, selected: s.name === styleName }));
        const sel = updated.find((s) => s.selected);
        setSelectedStyleName(sel?.name || styleName);
        setSelectedStyleContent(sel?.content || "");
        return updated;
      });
    } catch (error) {
      console.error("Failed to select user style:", error);
    }
  };
  const handleDraftFromEmail = async (additionalContext) => {
    try {
      const item = Office.context.mailbox.item;
      setIsLoading(true);
      // Get the current item (email) from the Outlook add-in context
      //const item = Office.context.mailbox.item;
      item.body.getAsync(
        "text",
        {
          asyncContext: {
            sender: item.sender.displayName,
            recepient: item.to[0].displayName,
            subject: item.subject,
            property: selectedOptionsRef.current[0],
          },
        },
        async function callback(result) {
          try {
            const text_content = formatEmailText(
              result.asyncContext.sender,
              result.asyncContext.recepient,
              result.asyncContext.subject,
              result.value
            );

            console.log("Drafting a response to the email:", text_content);
            if (additionalContext) {
              console.log("Additional context:", additionalContext);
            }
            console.log("Selected property:", result.asyncContext.property);
            const data = await postUserMessage(
              result.asyncContext.property,
              conversationId,
              additionalContext,
              authState.authToken,
              "outlookreply",
              selectedTab,
              text_content,
              Office.context.mailbox.userProfile.displayName,
              (item.from?.displayName === Office.context.mailbox.userProfile.displayName
                ? item.to?.[0]?.displayName
                : item.from?.displayName) || "",
              !searchChecked,
              getFileIds(),
              handlePollProgress
            );

            if (data.message && data.status === 200) {
              console.log("Message sent to bot successfully");
              console.log(data);
              setConversationId(data.message.conversation_id);

              // Check if the item is a message
              if (item.itemType === Office.MailboxEnums.ItemType.Message) {
                console.log("Content:", data.message.message.content);
                // A temporary fix to remove the signature from the email content
                const finalContent = cleanDraftText(data.message.message.content, officeType);
                localStorage.setItem(
                  "draftContext",
                  JSON.stringify({
                    conversationId: data.message.conversation_id,
                    officeConversationId: item.conversationId,
                    references: data.references,
                  })
                );
                if (selectedTab === "reply") {
                  item.displayReplyForm(finalContent);
                } else if (selectedTab === "replyall") {
                  item.displayReplyAllForm(finalContent);
                } else if (selectedTab === "forward") {
                  item.displayForwardForm(finalContent);
                }
              }
              if (Array.isArray(data.references) && data.references.length > 0) {
                handleBotResponse(
                  "I finished generating the draft and used the following data in the process:",
                  data.references,
                  data.message.id
                );
              } else {
                handleBotResponse("I finished generating the draft.", [], data.message.id);
              }
            } else {
              console.log("Error sending message to bot");
              console.log(data);
              if (data.message.message.content) {
                // Content moderation message
                handleBotResponse(data.message.message.content, [], data.message.id);
              } else {
                handleBotResponse("Sorry, I couldn't process creating a draft. Please try again.");
              }
            }
            setIsLoading(false);
          } catch (error) {
            console.error("Error while generating draft:", error);
            handleBotResponse("Sorry, I couldn't process creating a draft. Please try again.");
            setIsLoading(false);
          }
        }
      );
    } catch (error) {
      console.error("Error initiating draft:", error);
      setIsLoading(false);
    }
  };

  const handleDraftFromCompose = async (additionalContext) => {
    try {
      setIsLoading(true);
      const item = Office.context.mailbox.item;

      console.log("Drafting a new email:", additionalContext);
      console.log("Selected property:", selectedOptionsRef.current[0]);
      const data = await postUserMessage(
        selectedOptionsRef.current[0],
        conversationId,
        additionalContext,
        authState.authToken,
        "outlookcompose",
        "new",
        "",
        Office.context.mailbox.userProfile.displayName,
        "",
        !searchChecked,
        getFileIds(),
        handlePollProgress
      );

      if (data.message) {
        // we are in compose mode, inject the message to the compose window
        console.log("Message sent to bot successfully");
        console.log(data);
        setConversationId(data.message.conversation_id);
        var lines = data.message.message.content.split("\n");

        // Extract subject line if it exists
        var subjectLine = "";
        var remainingText = data.message.message.content;

        if (lines.length > 0 && lines[0].startsWith("Subject:")) {
          subjectLine = lines[0].replace("Subject:", "").trim();
          remainingText = lines.slice(1).join("\n");
        }

        // console.log("Content:", remainingText);

        // A temporary fix to remove the signature from the email content
        const finalContent = cleanDraftText(remainingText, officeType);

        item.body.setSelectedDataAsync(finalContent, { coercionType: "html" });
        if (subjectLine) {
          item.subject.setAsync(subjectLine);
        }
        if (Array.isArray(data.references) && data.references.length > 0) {
          handleBotResponse(
            "I finished generating the draft and used the following data in the process:",
            data.references,
            data.message.id
          );
        } else {
          handleBotResponse("I finished generating the draft.");
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error while generating draft:", error);
      handleBotResponse("Sorry, I couldn't process creating a draft. Please try again.");
      setIsLoading(false);
    }
  };

  const getWordContent = async () => {
    const text = await Word.run(async (context) => {
      // Get the current selection in the document
      const selection = context.document.getSelection();
      selection.load("text");

      await context.sync();

      if (selection.text.length > 0) {
        // If there is selected text, return it
        return selection.text;
      } else {
        return "";
      }
    });
    return text.split(/\s+/).slice(0, 500).join(" ");
  };

  const insertContentIntoWordDocument = async (content, { asHtml = false } = {}) => {
    if (!content?.trim()) {
      throw new Error("No content to insert");
    }

    if (asHtml) {
      try {
        await Word.run(async (context) => {
          const selection = context.document.getSelection();
          selection.insertHtml(content, Word.InsertLocation.replace);
          await context.sync();
        });
        console.log("HTML inserted successfully via Word.js API");
        return;
      } catch (wordApiError) {
        console.warn("Word.js HTML insert failed, falling back to setSelectedDataAsync:", wordApiError);
      }

      await new Promise((resolve, reject) => {
        Office.context.document.setSelectedDataAsync(
          content,
          { coercionType: Office.CoercionType.Html },
          (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              reject(new Error(asyncResult.error?.message || "Insert failed"));
            } else {
              console.log("HTML inserted successfully via setSelectedDataAsync");
              resolve();
            }
          }
        );
      });
      return;
    }

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.insertText(content, Word.InsertLocation.replace);
        await context.sync();
      });
      console.log("Text inserted successfully via Word.js API");
      return;
    } catch (wordApiError) {
      console.warn("Word.js insert failed, falling back to setSelectedDataAsync:", wordApiError);
    }

    await new Promise((resolve, reject) => {
      Office.context.document.setSelectedDataAsync(
        content,
        { coercionType: Office.CoercionType.Text },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(asyncResult.error?.message || "Insert failed"));
          } else {
            console.log("Text inserted successfully via setSelectedDataAsync");
            resolve();
          }
        }
      );
    });
  };

  const cleanDocumentText = (text) => {
    // Remove markdown formatting
    // Remove headers (e.g., # Heading)
    text = text.replace(/^#{1,6}\s+/gm, "");

    // Remove bold and italic (e.g., **bold**, _italic_)
    text = text.replace(/(\*\*|__)(.*?)\1/g, "$2"); // Bold
    text = text.replace(/(\*|_)(.*?)\1/g, "$2"); // Italic

    // Remove links (e.g., [link](http://example.com))
    text = text.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");

    // Remove inline code (e.g., `code`)
    text = text.replace(/`([^`]+)`/g, "$1");

    // Remove strikethrough (e.g., ~~strike~~)
    text = text.replace(/~~(.*?)~~/g, "$1");

    // Remove images (e.g., ![alt text](image_url))
    text = text.replace(/!\[.*?\]\(.*?\)/g, "");

    // Remove blockquotes (e.g., > Quote)
    text = text.replace(/^\s*>+\s?/gm, "");

    // Remove horizontal rules (e.g., --- or ***)
    text = text.replace(/\n-{3,}\n/g, "\n");
    text = text.replace(/\n\*{3,}\n/g, "\n");

    // Remove unordered list markers (e.g., - item, * item)
    text = text.replace(/^\s*[-*]\s+/gm, "");

    // Remove ordered list markers (e.g., 1. item)
    text = text.replace(/^\s*\d+\.\s+/gm, "");

    return text;
  };
  const handleDraftFromWord = async (additionalContext) => {
    try {
      setIsLoading(true);
      const text = await getWordContent();

      console.log("Drafting a new word doc:", additionalContext);
      console.log("Selected property:", selectedOptionsRef.current[0]);
      //(property_id, conversation_id, content, authToken, source, mode, document
      const data = await postUserMessage(
        selectedOptionsRef.current[0],
        conversationId,
        additionalContext,
        authState.authToken,
        "word",
        "composedoc",
        text,
        "",
        "",
        !searchChecked,
        getFileIds(),
        handlePollProgress
      );

      if (data.status_code === 200) {
        // we are in compose mode, inject the message to the compose window
        console.log("Message sent to bot successfully");
        console.log(data);
        setConversationId(data.response.conversation_id);

        var remainingText = data.response.answer;

        console.log("Content:", remainingText);

        if (plainTextChecked) {
          const finalContent = cleanDocumentText(remainingText);
          await insertContentIntoWordDocument(finalContent, { asHtml: false });
        } else {
          const finalContent = prepareRichDraftHtml(remainingText);
          await insertContentIntoWordDocument(finalContent, { asHtml: true });
        }

        if (Array.isArray(data.response.references) && data.response.references.length > 0) {
          handleBotResponse(
            "I finished generating the draft and used the following data in the process:",
            data.response.references,
            data.response.id
          );
        } else {
          handleBotResponse("I finished generating the draft.");
        }
        setIsLoading(false);
      } else if (data.status_code === 422) {
        console.log("Moderation applied to the message");
        console.log(data);
        handleBotResponse(data.message);
      } else {
        console.log("Error sending message to bot");
        console.log(data);
        handleBotResponse("Sorry, I couldn't process your message. Please try again.");
      }
    } catch (error) {
      console.error("Error while generating draft:", error);
      handleBotResponse("Sorry, I couldn't process creating a draft. Please try again.");
      setIsLoading(false);
    }
  };
  const onSignout = () => {
    logout();
  };
  const handleDraft = async (additionalContext) => {
    if (officeType === "OUTLOOK") {
      const item = Office.context.mailbox.item;
      if (typeof item.subject.setAsync === "function") {
        await handleDraftFromCompose(additionalContext);
      } else {
        await handleDraftFromEmail(additionalContext);
      }
    } else {
      await handleDraftFromWord(additionalContext);
    }
  };

  // Scroll to the last message when a message is added
  useEffect(() => {
    if (lastMessageRef.current) {
      requestAnimationFrame(() => {
        lastMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    }
  }, [messages]); // Re-run the effect whenever messages change

  useEffect(() => {
    if (!isLoading) {
      setLoadingStageText("");
    }
  }, [isLoading]);

  // Track Word selection to drive "Rewrite" label when applicable
  useEffect(() => {
    if (officeType !== "WORD") {
      setHasSelectionForRewrite(false);
      return;
    }
    let intervalId;
    const getSelection = () =>
      new Promise((resolve) => {
        try {
          Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              resolve(asyncResult.value || "");
            } else {
              resolve("");
            }
          });
        } catch (e) {
          resolve("");
        }
      });
    const update = async () => {
      const txt = (await getSelection()) || "";
      setHasSelectionForRewrite(!!(typeof txt === "string" && txt.trim() !== ""));
    };
    update();
    intervalId = setInterval(update, 1000);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedTab, officeType]);

  useEffect(() => {
    if (officeType !== "WORD") {
      return;
    }
    if (hasSelectionForRewrite !== prevRewriteModeRef.current) {
      setPlainTextChecked(hasSelectionForRewrite);
      prevRewriteModeRef.current = hasSelectionForRewrite;
    }
  }, [hasSelectionForRewrite, officeType]);

  // Fetch initial data when the component mounts
  useEffect(() => {
    const fetchDataEmailRead = async () => {
      const item = Office.context.mailbox.item;
      console.log("Fetching initial data for email:", item.from.emailAddress, item.to[0].emailAddress);
      const data = await fetchInitialData(item.from.emailAddress, item.to[0].emailAddress, authState.authToken);
      if (
        data.encodedFromEmail?.length > 0 ||
        data.sending_residents?.length > 0 ||
        data.recipient_properties?.length > 0
      ) {
        console.log(data);
        const residentsData = data.sending_residents;
        const recepientsData = data.recipient_properties;

        // Determine the property ID and name based on the data
        // We prioritize the recipient data if available
        const propertyId =
          recepientsData?.length > 0 ? recepientsData[0].id : residentsData[0]._resident_unit.property_id;
        const propertyName =
          recepientsData?.length > 0 ? recepientsData[0].name : residentsData[0]._resident_unit._unit_property.name;

        const propertyLegalDes =
          recepientsData?.length > 0
            ? recepientsData[0].legal_des
            : residentsData[0]._resident_unit._unit_property.legal_des;
        // Find the resident with the selected property ID
        const selectedResident = residentsData.find(
          (resident) => resident._resident_unit && resident._resident_unit.property_id === propertyId
        );
        let initialMessageText = "";
        // If we have a selected resident, use their name and unit
        // Otherwise, use the sender's name and the property name
        if (selectedResident) {
          const unit = selectedResident._resident_unit;
          initialMessageText = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
                                  with the email from ${selectedResident.name} living in ${unit._unit_property.name} \
                                  unit ${unit.name}. What would you like to do?`;
        } else {
          initialMessageText = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           with the email from ${item.from.displayName}`;
          if (propertyName) {
            initialMessageText += ` in the context of ${propertyName}`;
          }
          initialMessageText += `. \
           What would you like to do?`;
        }
        await addMessage(initialMessageText, "bot", {
          links: [],
        });

        await setSelectedOptions([propertyId.toString()]);
        await setSelectedValue(`${propertyName} (${propertyLegalDes})`);
        setResidents(residentsData);
      } else {
        const initialMessageText = `Hello ${authState.user.name}!  I am not sure which property this sender belongs to.\
        How can I help?`;
        await addMessage(initialMessageText, "bot", {
          links: [],
        });
      }
    };

    const fetchDataEmailCompose = async () => {
      const storedContext = localStorage.getItem("draftContext");
      const item = Office.context.mailbox.item;
      var localConversationId = 0;
      var references = [];
      if (storedContext !== null) {
        const originalContext = JSON.parse(storedContext);
        console.log("Original context:", originalContext);
        if (originalContext.officeConversationId === item.conversationId) {
          console.log("Found matching context:", originalContext.conversationId);
          setConversationId(originalContext.conversationId);
          localConversationId = originalContext.conversationId;
          references = originalContext.references;
          // clear the local storage
          localStorage.removeItem("draftContext");
        } else {
          console.log("items do not match: ", item.conversationId, originalContext.officeConversationId);
        }
      }

      if (localConversationId === 0) {
        const initialMessageText = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
        with composing a new email. What would you like to do?`;

        await addMessage(initialMessageText, "bot", {
          links: [],
        });
      } else {
        addMessage("I finished drafting the email and used the following data in the process:", "bot", {
          links: references,
        });
      }
    };
    const fetchData = async () => {
      try {
        hasFetchedInitialData.current = true;
        resetMessages();
        lastMessageRef.current = null;
        setConversationId(0);
        setUserInput("");
        setSelectedOptions([]);
        setSelectedValue("");
        setSelectedTab("new");
        const propData = await fetchProperties(authState.authToken);
        if (propData.length > 0) {
          const formattedItems = propData.map((item) => ({
            key: item.id.toString(),
            text: `${item.name} (${item.legal_des})`,
          }));
          console.log(formattedItems);
          await setProperties(formattedItems);
        } else {
          console.log("No properties found");
        }
        if (officeType == "OUTLOOK") {
          resetMessages();
          lastMessageRef.current = null;
          setConversationId(0);

          const subject = Office.context.mailbox.item.subject;
          if (typeof subject.setAsync === "function") {
            await fetchDataEmailCompose();
          } else {
            await fetchDataEmailRead();
          }
        } else {
          const initialMessageText = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
          with the document. What would you like to do?`;
          await addMessage(initialMessageText, "bot", {
            links: [],
          });
          postUsage("user-activation", "add-in-open", { source: "word" }, authState.authToken);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        // TBD: Handle errors here
      }
    };
    console.log("Current item inside useEffect:", currentItem);
    if (authState && authState.authToken && currentItem && officeType) {
      fetchData();
    }
  }, [authState?.authToken, currentItem]);

  // For debug purposes, log the selected options when they change
  useEffect(() => {
    console.log("Selected options:", selectedOptions);
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);

  return (
    <div className={styles.chatBotWord} id="chatbot">
      <div id="pinTooltipPlaceholder"></div>
      <div className={styles.messageList} id="messageList">
        {messages.map((message, index) => (
          <ChatMessage
            message={message}
            key={message.id}
            ref={index === messages.length - 1 ? lastMessageRef : null}
            onPostReferenceUsage={handlePostReferenceUsage}
          />
        ))}
      </div>
      <ComposeBox
        addinType={officeType}
        properties={properties}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        userPromptArea={userPromptArea}
        userInput={userInput}
        setUserInput={setUserInput}
        handleSendMessage={handleSendMessage}
        isReplyWindow={isReplyWindow}
        onNewTopic={onNewTopic}
        onSignout={onSignout}
        onPostUsage={onPostUsage}
        checkedTone={checkedTone}
        setCheckedTone={updateUserTone}
        userStyles={userStyles}
        selectedStyleName={selectedStyleName}
        onSelectStyle={updateUserStyle}
        searchChecked={searchChecked}
        onSearchCheckedChange={onSearchCheckedChange}
        hasSelectionForRewrite={hasSelectionForRewrite}
        plainTextChecked={plainTextChecked}
        onPlainTextCheckedChange={onPlainTextCheckedChange}
        fileContext={fileContext}
        fileUpload={fileUpload}
        onViewResource={handleViewResource}
      />
      {isLoading && (
        <div className={styles.overlay}>
          <Spinner className={styles.spinner} />
          {loadingStageText && (
            <div style={{ marginTop: 12, textAlign: "center", color: "#616161", fontSize: 12 }}>
              {loadingStageText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

UnicliChat.propTypes = {
  currentItem: PropTypes.object,
  officeType: PropTypes.string,
  currentUser: PropTypes.object,
};

export default UnicliChat;
